import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpEntrepriseAutocomplete from '../../components/kkp/KkpEntrepriseAutocomplete';
import PlanDeComWarningsAlert from '../../components/kkp/PlanDeComWarningsAlert';
import PlanDeComClientInfoModal from '../../components/kkp/PlanDeComClientInfoModal';
import PlanDeComDossierSwitcher from '../../components/kkp/PlanDeComDossierSwitcher';
import PlanDeComYearGrid from '../../components/kkp/PlanDeComYearGrid';
import PlanDeComMonthEditor from '../../components/kkp/PlanDeComMonthEditor';
import PlanDeComPreviewModal from '../../components/kkp/PlanDeComPreviewModal';
import PlanDeComPdfPreview from '../../components/kkp/PlanDeComPdfPreview';
import { AppFilterBar, AppFilterCommercialSelect, AppFilterField } from '../../components/filters';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import usePlanDeComSession from '../../hooks/usePlanDeComSession';
import useChiffrageDossierActions from '../../hooks/useChiffrageDossierActions';
import { useSetAssistantPageClient } from '../../context/AssistantPageClientContext';
import pdcService from '../../services/pdcService';
import { notify } from '../../components/common/AppToast';
import { MOIS_LABELS, buildCalendrierWarnings } from '../../utils/planDeComWarnings';
import {
  clientFormToSavePayload,
  dolibarrClientToDossierPatch,
  needsBdcClientInfoModal,
} from '../../utils/planDeComClientInfo';
import kkpService from '../../services/kkpService';
import {
  classifyPlanDeComSupport,
  planDeComMoisToNumber,
  resolvePlanDeComTypeCampagne,
  sanitizePlanDeComLigneSupports,
} from '../../utils/planDeComSupportUtils';
import {
  canEditPlanDeComSupports,
  formatPlanDeComMoney,
  getPlanDeComLigneSupportItems,
  getPlanDeComSupportsLockMessage,
  isPlanDeComLigneFilled,
  monthTitle,
  shiftPlanDeComCalendarWindow,
  syncLigneSupports,
} from '../../utils/planDeComCalendrierUi';
import { matchCommercialOption } from '../../utils/matchCommercialOption';
import { mergeDossierAfterSave } from '../../utils/mergeDossierAfterSave';
import useAppConfirm from '../../hooks/useAppConfirm';
import { AppAlert } from '../../components/ui';
import authService from '../../services/authService';
import '../../styles/kkp-planning-page.scss';

const compactLigneSupports = (ligne) => syncLigneSupports(
  ligne,
  getPlanDeComLigneSupportItems(ligne).map((item) => ({
    value: item.value,
    frais: item.feeLabel,
  })),
);

const syncSequentialPeriodsFromFirstLigne = (lignes, firstMonth, firstYear) => {
  const startMonthIndex = MOIS_LABELS.indexOf(String(firstMonth ?? '').trim().toUpperCase());
  if (!Array.isArray(lignes) || !lignes.length || startMonthIndex === -1 || !Number.isFinite(firstYear)) {
    return lignes;
  }

  const startYear = Number(firstYear);

  return lignes.map((ligne, index) => {
    const monthIndex = (startMonthIndex + index) % 12;
    const yearOffset = Math.floor((startMonthIndex + index) / 12);
    return {
      ...ligne,
      mois: MOIS_LABELS[monthIndex],
      annee: startYear + yearOffset,
    };
  });
};

const PlanDeComCalendrierPage = () => {
  const { ask, modal } = useAppConfirm();
  const {
    data: sessionData,
    dossiers,
    loading,
    error,
    saving,
    dossierId,
    save,
    reset,
    startNew,
    openDossier,
    duplicateDossier,
    deleteDossier,
    renameDossier,
    loadDossiers,
  } = usePlanDeComSession();
  const [resetting, setResetting] = useState(false);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [disponibilitesByKey, setDisponibilitesByKey] = useState({});
  const [disponibilitesLoading, setDisponibilitesLoading] = useState(false);
  const [generatingBdc, setGeneratingBdc] = useState(false);
  const [generatingMedia, setGeneratingMedia] = useState(false);
  const [generatingDiapo, setGeneratingDiapo] = useState(false);
  const [pushingDolibarr, setPushingDolibarr] = useState(false);
  const [clientInfoModalOpen, setClientInfoModalOpen] = useState(false);
  const [clientInfoPurpose, setClientInfoPurpose] = useState('download'); // 'download' | 'preview' | 'dolibarr'
  const [previewKind, setPreviewKind] = useState(null); // 'bdc' | 'media' | null
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [previewPdfError, setPreviewPdfError] = useState(null);
  const [previewBdcOptions, setPreviewBdcOptions] = useState(null);
  const [resolvingClient, setResolvingClient] = useState(false);
  const [editingOrdre, setEditingOrdre] = useState(null);
  const [syncWarnings, setSyncWarnings] = useState([]);
  const saveTimerRef = useRef(null);
  const dataInitializedRef = useRef(false);
  const lastDossierIdRef = useRef(null);
  const persistGenRef = useRef(0);
  const disponibilitesByKeyRef = useRef({});
  const disponibilitesLoadingKeysRef = useRef(new Set());
  const disponibilitesGenRef = useRef({});

  const assistantClientLabel = data?.dossier?.client_label
    || data?.dossier?.entreprise_label
    || '';
  useSetAssistantPageClient(assistantClientLabel);

  useEffect(() => {
    pdcService.getMeta().then(setMeta).catch((e) => setMetaError(e.message));
  }, []);

  useEffect(() => {
    if (!sessionData) return;
    const nextId = sessionData.dossier?.id ?? null;
    if (dataInitializedRef.current && nextId === lastDossierIdRef.current) return;
    lastDossierIdRef.current = nextId;
    setData(sessionData);
    setSyncWarnings(sessionData.planning_sync?.warnings ?? []);
    setEditingOrdre(null);
    disponibilitesByKeyRef.current = {};
    disponibilitesLoadingKeysRef.current = new Set();
    disponibilitesGenRef.current = {};
    setDisponibilitesByKey({});
    dataInitializedRef.current = true;
  }, [sessionData]);

  useEffect(() => {
    if (!data?.lignes) {
      setWarnings([]);
      return;
    }
    setWarnings(buildCalendrierWarnings(data.lignes));
  }, [data]);

  const disponibilitePeriods = useMemo(() => {
    const periods = new Map();
    (data?.lignes ?? []).forEach((ligne) => {
      const month = planDeComMoisToNumber(ligne.mois);
      if (!ligne.annee || !month) return;
      periods.set(`${ligne.annee}-${month}`, { annee: ligne.annee, mois: month });
    });
    return Array.from(periods.values());
  }, [data?.lignes]);

  const ensureDisponibilites = useCallback(async (annee, mois, { force = false } = {}) => {
    const year = Number(annee);
    const month = Number(mois);
    if (!year || !month) return;

    const key = `${year}-${month}`;
    if (!force && (disponibilitesByKeyRef.current[key] || disponibilitesLoadingKeysRef.current.has(key))) {
      return;
    }

    const gen = (disponibilitesGenRef.current[key] ?? 0) + 1;
    disponibilitesGenRef.current[key] = gen;
    disponibilitesLoadingKeysRef.current.add(key);
    setDisponibilitesLoading(true);
    try {
      const payload = await pdcService.getDisponibilites(year, month);
      if (disponibilitesGenRef.current[key] !== gen) return;
      setDisponibilitesByKey((prev) => {
        const next = { ...prev, [key]: payload };
        disponibilitesByKeyRef.current = next;
        return next;
      });
    } catch {
      // Un mois en échec ne doit pas vider le cache des autres.
    } finally {
      if (disponibilitesGenRef.current[key] === gen) {
        disponibilitesLoadingKeysRef.current.delete(key);
      }
      if (disponibilitesLoadingKeysRef.current.size === 0) {
        setDisponibilitesLoading(false);
      }
    }
  }, []);

  // Précharge en arrière-plan, sans tout annuler si un mois échoue.
  useEffect(() => {
    if (!disponibilitePeriods.length) return undefined;

    let cancelled = false;
    (async () => {
      for (const { annee, mois } of disponibilitePeriods) {
        if (cancelled) return;
        await ensureDisponibilites(annee, mois);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [disponibilitePeriods, ensureDisponibilites]);

  // Au moment d’ouvrir un mois, on force le chargement de ses dispos.
  useEffect(() => {
    if (editingOrdre == null || !data?.lignes) return;
    const ligne = data.lignes.find((item) => item.ordre === editingOrdre);
    if (!ligne) return;
    const month = planDeComMoisToNumber(ligne.mois);
    if (!ligne.annee || !month) return;
    ensureDisponibilites(ligne.annee, month, { force: true });
  }, [editingOrdre, data?.lignes, ensureDisponibilites]);

  const persist = useCallback((payload) => {
    if (!dossierId) return;
    const gen = ++persistGenRef.current;
    save(payload).then((full) => {
      if (!full || gen !== persistGenRef.current) return;
      setData((prev) => ({
        ...prev,
        lignes: full.lignes,
        recap: full.recap,
        totaux: full.totaux,
        dossier: mergeDossierAfterSave(prev?.dossier, full.dossier, payload),
      }));
      setSyncWarnings(full.planning_sync?.warnings ?? []);
    }).catch(() => {});
  }, [dossierId, save]);

  const scheduleSave = useCallback((nextPayload, delay = 120) => {
    if (!dossierId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(nextPayload), delay);
  }, [dossierId, persist]);

  const buildLignesPayload = (lignes) => lignes.map((l) => ({
    ordre: l.ordre,
    mois: l.mois,
    annee: l.annee,
    format_1: l.format_1,
    format_2: l.format_2,
    format_3: l.format_3,
    frais_crea: l.frais_crea,
    frais_redac: l.frais_redac,
    frais_klub: l.frais_klub,
    supports: (l.supports ?? getPlanDeComLigneSupportItems(l)).map((item) => ({
      value: item.value,
      frais: item.frais ?? item.feeLabel ?? null,
    })),
  }));

  const withResolvedTypeCampagne = (ligne) => ({
    ...ligne,
    type_campagne: resolvePlanDeComTypeCampagne(
      getPlanDeComLigneSupportItems(ligne).map((item) => item.value),
    ),
  });

  const updateDossierField = (field, value) => {
    if (!data?.dossier) return;
    const patch = { [field]: value };
    setData((prev) => ({
      ...prev,
      dossier: { ...prev.dossier, ...patch },
    }));
    const delay = field === 'titre' || field === 'client_label' ? 400 : 200;
    scheduleSave(patch, delay);
  };

  const supports = useMemo(() => {
    if (meta?.supports?.length) {
      return meta.supports;
    }

    const printLibelles = meta?.formats_print ?? meta?.formats ?? [];
    const webLibelles = meta?.formats_web ?? [];

    return [
      ...printLibelles.map((libelle) => ({
        libelle,
        kind: classifyPlanDeComSupport(libelle),
        actif: true,
      })),
      ...webLibelles.map((libelle) => ({
        libelle,
        kind: 'web',
        categorie: 'produit_web',
        actif: true,
      })),
    ].filter((support) => support.kind !== 'unknown');
  }, [meta]);

  const hasSelectedSupports = useMemo(
    () => (data?.lignes ?? []).some((ligne) => isPlanDeComLigneFilled(ligne)),
    [data?.lignes],
  );
  const canSelectSupports = canEditPlanDeComSupports(data?.dossier);
  const supportsLockMessage = getPlanDeComSupportsLockMessage(data?.dossier);

  useEffect(() => {
    if (!canSelectSupports && editingOrdre != null) {
      setEditingOrdre(null);
    }
  }, [canSelectSupports, editingOrdre]);

  const openMonthEditor = (ligne) => {
    if (!canSelectSupports) {
      notify.warning(supportsLockMessage);
      return;
    }
    setEditingOrdre(ligne.ordre);
  };

  const dossierOptions = useMemo(() => {
    const list = [...(dossiers ?? [])];
    const current = data?.dossier;
    if (current?.id && !list.some((item) => item.id === current.id)) {
      list.unshift(current);
    }
    return list;
  }, [dossiers, data?.dossier]);

  const updateLigne = (ordre, field, value) => {
    if (!data?.lignes) return;

    const isSupportField = field === 'supports'
      || ['format_1', 'format_2', 'format_3'].includes(field);
    if (isSupportField && !canSelectSupports) {
      notify.warning(supportsLockMessage);
      return;
    }

    if ((field === 'annee' || field === 'mois') && ordre === 0) {
      const firstLigne = data.lignes.find((ligne) => ligne.ordre === 0);
      const targetMois = field === 'mois' ? value : firstLigne?.mois;
      const targetAnnee = field === 'annee' ? Number(value) : Number(firstLigne?.annee);

      if (hasSelectedSupports) {
        const shifted = shiftPlanDeComCalendarWindow(data.lignes, targetMois, targetAnnee);
        if (!shifted.ok) {
          notify.warning(shifted.error || 'Impossible de décaler le calendrier.');
          return;
        }
        if (!shifted.shifted) {
          return;
        }

        const lignes = (shifted.lignes ?? []).map((ligne) => withResolvedTypeCampagne(ligne));
        const dossierPatch = {
          annee_debut: shifted.annee_debut,
          annee_fin: shifted.annee_fin,
        };
        setData((prev) => ({
          ...prev,
          lignes,
          dossier: { ...prev.dossier, ...dossierPatch },
        }));
        scheduleSave({
          ...dossierPatch,
          lignes: buildLignesPayload(lignes),
        }, 80);
        notify.success(
          `Calendrier décalé : début en ${monthTitle(targetMois)} ${targetAnnee}.`,
        );
        return;
      }

      let lignes = syncSequentialPeriodsFromFirstLigne(
        data.lignes,
        targetMois,
        targetAnnee,
      ).map((ligne) => withResolvedTypeCampagne(
        sanitizePlanDeComLigneSupports(ligne, supports, disponibilitesByKey),
      ));
      const dossierPatch = {
        annee_debut: lignes[0]?.annee ?? targetAnnee,
        annee_fin: lignes[lignes.length - 1]?.annee ?? targetAnnee,
      };
      setData((prev) => ({
        ...prev,
        lignes,
        dossier: { ...prev.dossier, ...dossierPatch },
      }));
      scheduleSave({
        ...dossierPatch,
        lignes: buildLignesPayload(lignes),
      }, 80);
      return;
    }

    let lignes = data.lignes.map((l) => {
      if (l.ordre !== ordre) return l;
      let updated = { ...l };
      if (field === 'supports') {
        updated = syncLigneSupports(updated, value);
      } else {
        const nextValue = field === 'annee' ? Number(value) : (value || null);
        updated = { ...updated, [field]: nextValue };
        if (isSupportField) {
          updated = compactLigneSupports(updated);
        }
      }
      updated = (field === 'annee' || field === 'mois')
        ? sanitizePlanDeComLigneSupports(updated, supports, disponibilitesByKey)
        : updated;
      return withResolvedTypeCampagne(updated);
    });
    setData((prev) => ({ ...prev, lignes }));
    scheduleSave({ lignes: buildLignesPayload(lignes) }, 80);
  };

  const clearMonth = (ordre) => {
    if (!data?.lignes) return;
    const lignes = data.lignes.map((l) => {
      if (l.ordre !== ordre) return l;
      return {
        ...l,
        type_campagne: null,
        format_1: null,
        format_2: null,
        format_3: null,
        frais_crea: null,
        frais_redac: null,
        frais_klub: null,
        supports: [],
      };
    });
    setData((prev) => ({ ...prev, lignes }));
    scheduleSave({ lignes: buildLignesPayload(lignes) }, 80);
  };

  const handleReset = async () => {
    if (!dossierId || resetting || saving) return;
    const ok = await ask({
      title: 'Réinitialiser ce chiffrage ?',
      message: 'Les campagnes du calendrier seront effacées. Les ventes encore en Option liées à ce chiffrage seront retirées des plannings. Les ventes déjà avancées (Vendu OK, etc.) resteront.',
      confirmLabel: 'Réinitialiser',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }
    setResetting(true);
    try {
      const full = await reset();
      if (full) {
        setData(full);
        setEditingOrdre(null);
      }
    } catch {
      // erreur déjà exposée via le hook
    } finally {
      setResetting(false);
    }
  };

  const {
    handleOpenDossier,
    handleNewDossier,
    handleDuplicateDossier,
    handleDeleteDossier,
    handleRenameDossier,
  } = useChiffrageDossierActions({
    session: {
      dossierId,
      saving,
      openDossier,
      startNew,
      duplicateDossier,
      deleteDossier,
      renameDossier,
    },
    ask,
    saveTimerRef,
    flushBeforeSwitch: async () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    onSessionApplied: (full, ctx) => {
      setData(full);
      if (ctx?.action === 'rename') return;
      if (ctx?.action === 'new') {
        setSyncWarnings([]);
      } else {
        setSyncWarnings(full.planning_sync?.warnings ?? []);
      }
      setEditingOrdre(null);
    },
    labels: {
      deleteMessage:
        'Les ventes encore en Option liées à ce chiffrage seront retirées des plannings. Les ventes déjà avancées resteront.',
    },
  });

  const handleEntrepriseSelect = ({
    id,
    nom,
    client_label: clientLabel,
    commercial_id: commercialId,
    commercial_nom: commercialNom,
    commercial_email: commercialEmail,
    commercial_emails: commercialEmails,
    adresse,
    cp,
    ville,
    telephone,
    email,
  }) => {
    if (!data?.dossier) return;
    const companyName = String(nom ?? '').trim();
    const contactName = String(clientLabel ?? '').trim();
    const patch = {
      entreprise_label: companyName || null,
      dolibarr_thirdparty_id: id ?? null,
      client_label: contactName || null,
      client_adresse: adresse ?? null,
      client_cp: cp ?? null,
      client_ville: ville ?? null,
      client_telephone: telephone ?? null,
      client_email: email ?? null,
    };
    const matchedCommercial = matchCommercialOption(
      (meta?.commerciaux ?? []).map((c) => ({
        value: String(c.id),
        label: c.nom,
        email: c.email,
      })),
      {
        commercial_id: commercialId,
        commercial_nom: commercialNom,
        commercial_email: commercialEmail,
        commercial_emails: commercialEmails,
      },
    );
    if (matchedCommercial) {
      patch.commercial_id = Number(matchedCommercial.value);
    }
    setData((prev) => ({
      ...prev,
      dossier: { ...prev.dossier, ...patch },
    }));
    persist(patch);
  };

  const handleEntrepriseResolving = (resolving) => {
    setResolvingClient(resolving);
    if (!resolving) return;
    // Affichage immédiat : on vide le nom client le temps de la recherche Dolibarr.
    setData((prev) => (
      prev
        ? { ...prev, dossier: { ...prev.dossier, client_label: '' } }
        : prev
    ));
  };

  const flushPendingSave = useCallback(async (extraPayload = {}) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (!dossierId || !data) return null;
    const dossier = data.dossier ?? {};
    return save({
      entreprise_label: dossier.entreprise_label ?? null,
      client_label: dossier.client_label ?? null,
      dolibarr_thirdparty_id: dossier.dolibarr_thirdparty_id ?? null,
      client_adresse: dossier.client_adresse ?? null,
      client_cp: dossier.client_cp ?? null,
      client_ville: dossier.client_ville ?? null,
      client_telephone: dossier.client_telephone ?? null,
      client_email: dossier.client_email ?? null,
      commercial_id: dossier.commercial_id ?? null,
      abonnement_klub: Boolean(dossier.abonnement_klub),
      lignes: buildLignesPayload(data.lignes ?? []),
      ...extraPayload,
    });
  }, [dossierId, data, save]);

  const revokePreviewPdfUrl = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const closePreview = useCallback(() => {
    setPreviewKind(null);
    setPreviewPdfError(null);
    setPreviewPdfLoading(false);
    setPreviewBdcOptions(null);
    revokePreviewPdfUrl();
  }, [revokePreviewPdfUrl]);

  const openPdfPreview = useCallback(async ({ kind, fetchPdf, bdcOptions = null }) => {
    setPreviewKind(kind);
    setPreviewPdfLoading(true);
    setPreviewPdfError(null);
    setPreviewBdcOptions(bdcOptions);
    revokePreviewPdfUrl();
    try {
      const { blob } = await fetchPdf();
      setPreviewPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      setPreviewPdfError(err?.message ?? 'Impossible de charger l’aperçu PDF');
      throw err;
    } finally {
      setPreviewPdfLoading(false);
    }
  }, [revokePreviewPdfUrl]);

  const openClientInfoForBdc = useCallback(async (purpose) => {
    if (!dossierId || generatingBdc || previewPdfLoading || pushingDolibarr) return;

    if (purpose === 'dolibarr') {
      if (!meta?.can_push_dolibarr) {
        notify.warning('API Dolibarr non configurée.');
        return;
      }
      if (!data?.dossier?.dolibarr_thirdparty_id) {
        notify.warning('Sélectionnez un client dans la liste Dolibarr (champ Entreprise).');
        return;
      }
      if (!data?.dossier?.commercial_id) {
        notify.warning('Sélectionnez un commercial avant d’envoyer vers Dolibarr.');
        return;
      }
      const hasCampaign = (data?.lignes ?? []).some((l) => isPlanDeComLigneFilled(l));
      if (!hasCampaign) {
        notify.warning('Ajoutez au moins une campagne au calendrier avant d’envoyer vers Dolibarr.');
        return;
      }
    }

    let dossier = data?.dossier ?? null;
    const thirdpartyId = Number(dossier?.dolibarr_thirdparty_id) || 0;

    if (needsBdcClientInfoModal(dossier) && thirdpartyId > 0) {
      try {
        const client = await kkpService.getDolibarrClient(thirdpartyId);
        const patch = dolibarrClientToDossierPatch(client, dossier);
        setData((prev) => (
          prev
            ? { ...prev, dossier: { ...prev.dossier, ...patch } }
            : prev
        ));
        await persist(patch);
      } catch {
        // On laisse le modal gérer la saisie manuelle.
      }
    }

    setClientInfoPurpose(purpose);
    setClientInfoModalOpen(true);
  }, [
    dossierId,
    generatingBdc,
    previewPdfLoading,
    pushingDolibarr,
    meta?.can_push_dolibarr,
    data?.dossier,
    data?.lignes,
    persist,
  ]);

  const runBdcFromClientModal = useCallback(async ({ client = null, bdcOptions = {} } = {}) => {
    if (!dossierId || generatingBdc || previewPdfLoading || pushingDolibarr) return;
    const isPreview = clientInfoPurpose === 'preview';
    const isDolibarr = clientInfoPurpose === 'dolibarr';

    if (isDolibarr) {
      setPushingDolibarr(true);
      try {
        await notify.run(async () => {
          const full = await flushPendingSave(client ? clientFormToSavePayload(client) : {});
          if (full) setData(full);
          const res = await pdcService.pushDolibarr(dossierId, bdcOptions);
          setClientInfoModalOpen(false);
          if (res?.propal_url) {
            window.open(res.propal_url, '_blank', 'noopener,noreferrer');
          }
          return res;
        }, {
          loading: 'Création de la propal Dolibarr…',
          success: (res) => `Propal ${res.propal_ref || `#${res.propal_id}`} créée pour ${res.client_name}`,
        });
      } catch {
        // Toast déjà affiché par notify.run
      } finally {
        setPushingDolibarr(false);
      }
      return;
    }

    if (isPreview) {
      setGeneratingBdc(true);
      try {
        await notify.run(async () => {
          const full = await flushPendingSave(client ? clientFormToSavePayload(client) : {});
          if (full) setData(full);
          await pdcService.prepareBdc(dossierId);
          setClientInfoModalOpen(false);
          await openPdfPreview({
            kind: 'bdc',
            bdcOptions,
            fetchPdf: () => pdcService.fetchBdcPdf(dossierId, bdcOptions),
          });
        }, {
          loading: 'Préparation de l’aperçu…',
          success: 'Aperçu PDF prêt',
        });
      } catch {
        // Toast déjà affiché par notify.run
      } finally {
        setGeneratingBdc(false);
      }
      return;
    }

    setGeneratingBdc(true);
    try {
      await notify.run(async () => {
        const full = await flushPendingSave(client ? clientFormToSavePayload(client) : {});
        if (full) setData(full);
        await pdcService.prepareBdc(dossierId);
        await pdcService.downloadBdcPdf(dossierId, bdcOptions);
        setClientInfoModalOpen(false);
      }, {
        loading: 'Préparation du devis…',
        success: 'Devis téléchargé',
      });
    } catch {
      // Toast déjà affiché par notify.run
    } finally {
      setGeneratingBdc(false);
    }
  }, [
    dossierId,
    generatingBdc,
    previewPdfLoading,
    pushingDolibarr,
    clientInfoPurpose,
    flushPendingSave,
    openPdfPreview,
  ]);

  const handleGenerateBdc = useCallback(() => openClientInfoForBdc('download'), [openClientInfoForBdc]);

  const handlePushDolibarr = useCallback(() => openClientInfoForBdc('dolibarr'), [openClientInfoForBdc]);

  const downloadPreviewPdf = useCallback(async () => {
    if (!dossierId || !previewKind) return;
    if (previewKind === 'bdc') {
      if (generatingBdc) return;
      setGeneratingBdc(true);
      try {
        await notify.run(async () => {
          await pdcService.downloadBdcPdf(dossierId, previewBdcOptions ?? {});
        }, {
          loading: 'Téléchargement du devis…',
          success: 'Devis téléchargé',
        });
      } catch {
        // Toast déjà affiché par notify.run
      } finally {
        setGeneratingBdc(false);
      }
      return;
    }

    if (generatingMedia) return;
    setGeneratingMedia(true);
    try {
      await notify.run(async () => {
        await pdcService.downloadMediaPlanningPdf(dossierId);
      }, {
        loading: 'Téléchargement du planning média…',
        success: 'Planning média téléchargé',
      });
    } catch {
      // Toast déjà affiché par notify.run
    } finally {
      setGeneratingMedia(false);
    }
  }, [dossierId, previewKind, previewBdcOptions, generatingBdc, generatingMedia]);

  const handleGenerateMedia = useCallback(async () => {
    if (!dossierId || generatingMedia) return;
    const hasCampaign = (data?.lignes ?? []).some((l) => isPlanDeComLigneFilled(l));
    if (!hasCampaign) {
      notify.warning('Ajoutez au moins une campagne au calendrier avant de télécharger le planning média.');
      return;
    }
    setGeneratingMedia(true);
    try {
      await notify.run(async () => {
        const full = await flushPendingSave();
        if (full) setData(full);
        await pdcService.downloadMediaPlanningPdf(dossierId);
      }, {
        loading: 'Préparation du planning média…',
        success: 'Planning média téléchargé',
      });
    } catch {
      // Toast déjà affiché par notify.run
    } finally {
      setGeneratingMedia(false);
    }
  }, [dossierId, generatingMedia, data?.lignes, flushPendingSave]);

  const openBdcPreview = useCallback(() => openClientInfoForBdc('preview'), [openClientInfoForBdc]);

  const openMediaPreview = useCallback(async () => {
    if (!dossierId || previewPdfLoading || generatingMedia) return;
    const hasCampaign = (data?.lignes ?? []).some((l) => isPlanDeComLigneFilled(l));
    if (!hasCampaign) {
      notify.warning('Ajoutez au moins une campagne au calendrier avant d’afficher le planning média.');
      return;
    }
    try {
      await notify.run(async () => {
        const full = await flushPendingSave();
        if (full) setData(full);
        await openPdfPreview({
          kind: 'media',
          fetchPdf: () => pdcService.fetchMediaPlanningPdf(dossierId),
        });
      }, {
        loading: 'Préparation de l’aperçu…',
        success: 'Aperçu PDF prêt',
      });
    } catch {
      // Toast déjà affiché par notify.run
      closePreview();
    }
  }, [
    dossierId,
    previewPdfLoading,
    generatingMedia,
    data?.lignes,
    flushPendingSave,
    openPdfPreview,
    closePreview,
  ]);

  const toolsBusy = generatingBdc || generatingMedia || generatingDiapo || pushingDolibarr || saving || previewPdfLoading;

  const handleGenerateDiapo = useCallback(async () => {
    if (!dossierId || generatingDiapo) return;
    const hasCampaign = (data?.lignes ?? []).some((l) => isPlanDeComLigneFilled(l));
    if (!hasCampaign) {
      notify.warning('Ajoutez au moins une campagne au calendrier avant de générer le diaporama.');
      return;
    }
    setGeneratingDiapo(true);
    try {
      const result = await notify.run(async () => {
        const full = await flushPendingSave();
        if (full) setData(full);
        return pdcService.generateDiapo(dossierId);
      }, {
        loading: 'Génération du diaporama…',
        success: 'Diaporama généré',
      });
      if (result?.presentation_url) {
        window.open(result.presentation_url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // Toast déjà affiché par notify.run
    } finally {
      setGeneratingDiapo(false);
    }
  }, [dossierId, generatingDiapo, data?.lignes, flushPendingSave]);

  const commercialOptions = useMemo(
    () => (meta?.commerciaux ?? []).map((c) => ({ value: String(c.id), label: c.nom })),
    [meta],
  );
  const displayError = error || metaError;
  const editingLigne = useMemo(
    () => (data?.lignes ?? []).find((ligne) => ligne.ordre === editingOrdre) ?? null,
    [data?.lignes, editingOrdre],
  );

  return (
    <MasterLayout>
      <Breadcrumb
        title="Chiffrage Koikispass"
        subtitle="Placez vos campagnes mois par mois, puis générez le devis"
        actions={(
          <KkpPlanningPageActions
            actions={[
              {
                key: 'push-dolibarr',
                label: pushingDolibarr ? 'Envoi Dolibarr…' : 'Envoyer vers Dolibarr',
                icon: 'mdi:cloud-upload-outline',
                onClick: handlePushDolibarr,
                disabled: !dossierId || toolsBusy || !meta?.can_push_dolibarr,
                loading: pushingDolibarr,
              },
              {
                key: 'generate-diapo',
                label: generatingDiapo ? 'Génération…' : 'Générer le diaporama',
                icon: 'mdi:presentation',
                onClick: handleGenerateDiapo,
                disabled: !dossierId || toolsBusy,
                loading: generatingDiapo,
              },
              {
                key: 'preview-bdc',
                label: 'Aperçu bon de commande',
                icon: 'mdi:file-document-outline',
                onClick: openBdcPreview,
                disabled: !dossierId || toolsBusy,
              },
              {
                key: 'preview-media',
                label: 'Aperçu media planning',
                icon: 'mdi:television-guide',
                onClick: openMediaPreview,
                disabled: !dossierId || toolsBusy,
              },
              {
                key: 'reset',
                label: resetting ? 'Réinitialisation…' : 'Tout effacer',
                icon: 'mdi:refresh',
                onClick: handleReset,
                disabled: !dossierId || resetting || toolsBusy,
                loading: resetting,
                variant: 'outline-danger',
              },
            ]}
            menuItems={[
              {
                key: 'generate-bdc',
                label: generatingBdc ? 'Préparation du devis…' : 'Télécharger le devis',
                icon: 'mdi:file-pdf-box',
                onClick: handleGenerateBdc,
                disabled: !dossierId || toolsBusy,
              },
              {
                key: 'generate-media',
                label: generatingMedia ? 'Préparation du planning…' : 'Télécharger le media planning',
                icon: 'mdi:file-pdf-box',
                onClick: handleGenerateMedia,
                disabled: !dossierId || toolsBusy,
              },
            ]}
          >
            <span className={`badge ${saving ? 'bg-light text-secondary' : 'bg-success-subtle text-success'}`}>
              {saving ? 'Enregistrement…' : 'À jour'}
            </span>
          </KkpPlanningPageActions>
        )}
      />

      {displayError && (
        <AppAlert>{displayError}</AppAlert>
      )}
      <PlanDeComWarningsAlert
        warnings={[...warnings, ...syncWarnings]}
        title="Points à vérifier"
        compact
      />

      <PlanDeComClientInfoModal
        show={clientInfoModalOpen}
        dossier={data?.dossier}
        meta={meta}
        title={
          clientInfoPurpose === 'preview'
            ? 'Aperçu du document'
            : clientInfoPurpose === 'dolibarr'
              ? 'Envoyer vers Dolibarr'
              : 'Générer le document'
        }
        confirmLabel={
          clientInfoPurpose === 'preview'
            ? 'Voir l’aperçu PDF'
            : clientInfoPurpose === 'dolibarr'
              ? 'Créer la propal'
              : 'Télécharger'
        }
        confirmIcon={clientInfoPurpose === 'dolibarr' ? 'mdi:cloud-upload-outline' : 'mdi:file-pdf-box'}
        submitting={generatingBdc || previewPdfLoading || pushingDolibarr}
        onClose={() => !(generatingBdc || previewPdfLoading || pushingDolibarr) && setClientInfoModalOpen(false)}
        onConfirm={runBdcFromClientModal}
      />

      <PlanDeComPreviewModal
        show={previewKind === 'bdc'}
        title="Aperçu bon de commande"
        subtitle="Aperçu exact du PDF téléchargeable"
        onClose={closePreview}
        primaryAction={{
          label: generatingBdc ? 'Téléchargement…' : 'Télécharger le PDF',
          icon: 'mdi:file-pdf-box',
          disabled: !dossierId || generatingBdc || previewPdfLoading || !previewPdfUrl,
          onClick: downloadPreviewPdf,
        }}
      >
        <PlanDeComPdfPreview
          url={previewPdfUrl}
          loading={previewPdfLoading}
          error={previewPdfError}
          emptyMessage="Aucune campagne — complétez d’abord le calendrier"
        />
      </PlanDeComPreviewModal>

      <PlanDeComPreviewModal
        show={previewKind === 'media'}
        title="Aperçu media planning"
        subtitle="Aperçu exact du PDF téléchargeable"
        onClose={closePreview}
        primaryAction={{
          label: generatingMedia ? 'Téléchargement…' : 'Télécharger le PDF',
          icon: 'mdi:file-pdf-box',
          disabled: !dossierId || generatingMedia || previewPdfLoading || !previewPdfUrl,
          onClick: downloadPreviewPdf,
        }}
      >
        <PlanDeComPdfPreview
          url={previewPdfUrl}
          loading={previewPdfLoading}
          error={previewPdfError}
          landscape
          emptyMessage="Aucune campagne à afficher"
        />
      </PlanDeComPreviewModal>

      <PlanDeComMonthEditor
        show={editingOrdre != null}
        ligne={editingLigne}
        meta={meta}
        disponibilitesByKey={disponibilitesByKey}
        disponibilitesLoading={disponibilitesLoading}
        onClose={() => setEditingOrdre(null)}
        onChangeField={updateLigne}
        onClearMonth={clearMonth}
      />

      {loading && !data && <KkpPlanningEmptyState loading />}

      {data && (
        <>
          {supportsLockMessage && (
            <AppAlert variant="warning" className="py-2 mb-3" role="status">
              {supportsLockMessage}
            </AppAlert>
          )}
          <PlanDeComDossierSwitcher
            dossiers={dossierOptions}
            current={data.dossier}
            currentId={dossierId}
            disabled={saving || resetting || toolsBusy}
            saving={saving}
            canSeeTeamDossiers={authService.isAdminCommercial() || authService.isSuperAdmin()}
            commerciaux={meta?.commerciaux ?? []}
            onRefreshDossiers={loadDossiers}
            onOpen={handleOpenDossier}
            onNew={handleNewDossier}
            onDuplicate={handleDuplicateDossier}
            onDelete={handleDeleteDossier}
            onRename={handleRenameDossier}
          />
          <AppFilterBar showReset={false}>
            <AppFilterField col="col-12 col-md-6 col-lg-3" label="Titre du chiffrage">
              <input
                type="text"
                className="form-control form-control-sm"
                value={data.dossier.titre ?? ''}
                onChange={(e) => updateDossierField('titre', e.target.value)}
                onBlur={(e) => updateDossierField('titre', e.target.value.trim() || null)}
                placeholder="Ex. Campagne printemps ACME"
                autoComplete="off"
              />
            </AppFilterField>
            <AppFilterField
              col="col-12 col-md-6 col-lg-3"
              className={!data.dossier.entreprise_label ? 'kkp-pdc-field--missing' : ''}
              label={(
                <>
                  Entreprise
                  <span className="kkp-pdc-required" aria-hidden="true"> *</span>
                </>
              )}
            >
              <KkpEntrepriseAutocomplete
                value={data.dossier.entreprise_label ?? ''}
                onSelect={handleEntrepriseSelect}
                onResolvingChange={handleEntrepriseResolving}
                placeholder="Rechercher dans Dolibarr…"
                inputClassName="form-control form-control-sm"
              />
            </AppFilterField>
            <AppFilterField col="col-12 col-md-6 col-lg-2" label="Nom du client">
              <input
                type="text"
                className="form-control form-control-sm"
                value={data.dossier.client_label ?? ''}
                onChange={(e) => updateDossierField('client_label', e.target.value)}
                onBlur={(e) => updateDossierField('client_label', e.target.value.trim() || null)}
                placeholder={
                  resolvingClient
                    ? 'Recherche contact Dolibarr…'
                    : 'Contact rattaché ou saisie libre…'
                }
                disabled={resolvingClient}
                autoComplete="off"
              />
            </AppFilterField>
            <AppFilterCommercialSelect
              id="pdc-commercial"
              col="col-6 col-md-4 col-lg-2"
              className={data.dossier.commercial_id == null ? 'kkp-pdc-field--missing' : ''}
              label={(
                <>
                  Commercial
                  <span className="kkp-pdc-required" aria-hidden="true"> *</span>
                </>
              )}
              value={data.dossier.commercial_id != null ? [String(data.dossier.commercial_id)] : []}
              onChange={(next) => updateDossierField(
                'commercial_id',
                next[0] ? Number(next[0]) : null,
              )}
              options={commercialOptions}
              emptyLabel="Aucun commercial trouvé"
            />
          </AppFilterBar>

          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 px-1">
            <div className="form-check form-switch mb-0 d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="pdc-abo-klub"
                checked={Boolean(data.dossier.abonnement_klub)}
                onChange={(e) => {
                  const patch = { abonnement_klub: e.target.checked };
                  setData((prev) => ({
                    ...prev,
                    dossier: { ...prev.dossier, ...patch },
                  }));
                  scheduleSave(patch, 200);
                }}
              />
              <label className="form-check-label small" htmlFor="pdc-abo-klub">Abonnement Klub (−5 %)</label>
            </div>
          </div>

          <div className="card kkp-pdc-calendar-card kkp-planning-results-card">
            <div className="card-body">
              <PlanDeComYearGrid
                lignes={data.lignes ?? []}
                locked={!canSelectSupports}
                onOpenMonth={openMonthEditor}
              />
            </div>
          </div>

          {data.recap && (
            <div className="card mt-3 kkp-pdc-recap-card">
              <div className="card-body">
                <div className="kkp-pdc-recap-header">
                  <h2 className="kkp-pdc-recap-title">Récapitulatif</h2>
                  <p className="kkp-pdc-recap-subtitle">Montants calculés à partir de votre calendrier</p>
                </div>
                <div className="kkp-pdc-recap-hero">
                  <div className="kkp-pdc-recap-hero__item">
                    <div className="kkp-pdc-recap-label">Total HT</div>
                    <div className="kkp-pdc-recap-value kkp-pdc-recap-value--primary">
                      {formatPlanDeComMoney(data.recap.budget_global_net_ht)}
                    </div>
                  </div>
                  <div className="kkp-pdc-recap-hero__item">
                    <div className="kkp-pdc-recap-label">Total TTC</div>
                    <div className="kkp-pdc-recap-value kkp-pdc-recap-value--primary">
                      {formatPlanDeComMoney(data.recap.budget_global_net_ttc)}
                    </div>
                  </div>
                </div>
                <div className="kkp-pdc-recap-details">
                  <div className="kkp-pdc-recap-detail">
                    <div className="kkp-pdc-recap-label">Prix catalogue</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(data.recap.total_brut)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail">
                    {Number(data.recap.taux_remise_multi_campagnes) > 0 ? (
                      <span className="kkp-pdc-recap-ribbon" aria-label={`Remise ${data.recap.taux_remise_multi_campagnes} %`}>
                        −{Number(data.recap.taux_remise_multi_campagnes).toLocaleString('fr-FR')}%
                      </span>
                    ) : null}
                    <div className="kkp-pdc-recap-label">Remise multi-campagnes</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(data.recap.remise_multi_campagnes)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail">
                    {Number(data.recap.remise_multi_news) !== 0 && Number(data.recap.taux_remise_multi_news ?? data.recap.taux_remise_multi_campagnes) > 0 ? (
                      <span className="kkp-pdc-recap-ribbon" aria-label={`Remise multi-news ${data.recap.taux_remise_multi_news ?? data.recap.taux_remise_multi_campagnes} %`}>
                        −{Number(data.recap.taux_remise_multi_news ?? data.recap.taux_remise_multi_campagnes).toLocaleString('fr-FR')}%
                      </span>
                    ) : null}
                    <div className="kkp-pdc-recap-label">Remise multi-news</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(data.recap.remise_multi_news)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail">
                    <div className="kkp-pdc-recap-label">Remise Duo / Trio</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(data.recap.remise_duo_trio)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail kkp-pdc-recap-detail--multi">
                    {Boolean(data.recap.abonnement_klub) ? (
                      <span className="kkp-pdc-recap-ribbon" aria-label="Remise Klub 5 %">
                        −5%
                      </span>
                    ) : null}
                    <div className="kkp-pdc-recap-label">Bonus Klub</div>
                    <div className="kkp-pdc-recap-value">{formatPlanDeComMoney(data.recap.remise_bonus_klub)}</div>
                  </div>
                  <div className="kkp-pdc-recap-detail">
                    <div className="kkp-pdc-recap-label">Frais techniques</div>
                    <div className="kkp-pdc-recap-value">
                      {formatPlanDeComMoney(Number(data.totaux?.AC) || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {modal}
    </MasterLayout>
  );
};

export default PlanDeComCalendrierPage;
